import type { IntegerAmount } from '@actual-app/core/shared/util';
/**
 * Reparto del paso 7 de la «Primera travesía»: el «Auto» de Gavi y lo que
 * dice tras cada cambio. Funciones puras, importes en céntimos. Copia de
 * `autoRepartir` y `fijarAsignado` de la maqueta aprobada.
 */
import type { TFunction } from 'i18next';

import { MENSUALES, OCASIONALES, sugeridoCofre } from './catalogo';
import type { IdsTravesia } from './estado';
import type { ContextoTrato } from './trato';

/** Un cofre del Plan durante el reparto (id real de la categoría). */
export type CofreReparto = {
  id: string;
  /** Lo sugerido este mes (céntimos). */
  sugerido: IntegerAmount;
  /** La meta: se lleva lo que sobra. */
  esMeta?: boolean;
};

export type Asignado = Readonly<Record<string, IntegerAmount>>;

function suma(asignado: Asignado, cofres: readonly CofreReparto[]) {
  return cofres.reduce((a, c) => a + (asignado[c.id] ?? 0), 0);
}

/**
 * «Listos para asignar» con lo asignado a los cofres: `dinero` (saldo de
 * las cuentas del presupuesto) menos lo asignado.
 */
export function restante(
  cofres: readonly CofreReparto[],
  asignado: Asignado,
  dinero: IntegerAmount,
): IntegerAmount {
  return dinero - suma(asignado, cofres);
}

/**
 * «Auto»: a cada cofre (salvo la meta) le pone lo que le falta hasta lo
 * sugerido mientras haya dinero; lo que sobra va a la meta (o, sin meta, al
 * último cofre). Si ya había asignado de más, recorta del final hacia
 * atrás. Devuelve lo asignado a cada cofre tras el reparto: `restante` queda
 * en 0 siempre que `dinero` ≥ 0 y nunca hay importes negativos.
 *
 * @param dinero Dinero total a repartir (en la app: «Listos para asignar»
 *   más lo ya asignado a estos cofres).
 */
export function repartoAuto(
  cofres: readonly CofreReparto[],
  asignado: Asignado,
  dinero: IntegerAmount,
): Record<string, IntegerAmount> {
  const res: Record<string, IntegerAmount> = {};
  for (const c of cofres) {
    res[c.id] = Math.max(0, asignado[c.id] ?? 0);
  }
  let queda = dinero - suma(res, cofres);
  for (const c of cofres) {
    if (c.esMeta) {
      continue;
    }
    const ya = res[c.id];
    if (ya >= c.sugerido) {
      continue;
    }
    const pon = Math.max(0, Math.min(c.sugerido - ya, queda));
    res[c.id] = ya + pon;
    queda -= pon;
  }
  if (queda > 0) {
    const destino = cofres.find(c => c.esMeta) ?? cofres[cofres.length - 1];
    if (destino) {
      res[destino.id] += queda;
      queda = 0;
    }
  }
  if (queda < 0) {
    for (const c of [...cofres].reverse()) {
      const quita = Math.min(res[c.id], -queda);
      res[c.id] -= quita;
      queda += quita;
      if (queda >= 0) {
        break;
      }
    }
  }
  return res;
}

/** Importes que cambian entre dos repartos, para `budget/budget-amounts`. */
export function cambiosReparto(
  antes: Asignado,
  despues: Asignado,
): { category: string; amount: IntegerAmount }[] {
  return Object.entries(despues)
    .filter(([id, amount]) => (antes[id] ?? 0) !== amount)
    .map(([category, amount]) => ({ category, amount }));
}

export type MensajeReparto =
  /** Asignado de más: «¡Agua en la bodega!». */
  | { tipo: 'demas'; gavi: 'tapa'; importe: IntegerAmount; autoPulsa: boolean }
  /** Llega a 0 viniendo de otro valor: aplaude y destellos. */
  | { tipo: 'cero'; gavi: 'aplaude'; importe: 0; autoPulsa: false }
  /** Desde el 2.º cambio a mano: propone Auto. */
  | { tipo: 'aburre'; gavi: 'ladea'; importe: IntegerAmount; autoPulsa: true }
  /** Sigue repartiendo: «Quedan X». */
  | { tipo: 'sigue'; gavi: 'ladea'; importe: IntegerAmount; autoPulsa: false };

/**
 * Lo que dice Gavi tras un cambio en el paso 7.
 *
 * @param rAntes «Listos para asignar» antes del cambio (céntimos).
 * @param r «Listos para asignar» después.
 * @param cambios Cambios hechos a mano, contando este (desde el 2.º, Auto pulsa).
 */
export function mensajeReparto(
  rAntes: IntegerAmount,
  r: IntegerAmount,
  cambios: number,
): MensajeReparto {
  if (r < 0) {
    return {
      tipo: 'demas',
      gavi: 'tapa',
      importe: -r,
      autoPulsa: cambios >= 2,
    };
  }
  if (r === 0 && rAntes !== 0) {
    return { tipo: 'cero', gavi: 'aplaude', importe: 0, autoPulsa: false };
  }
  if (cambios >= 2) {
    return { tipo: 'aburre', gavi: 'ladea', importe: r, autoPulsa: true };
  }
  return { tipo: 'sigue', gavi: 'ladea', importe: r, autoPulsa: false };
}

/**
 * Texto del bocadillo para un `MensajeReparto`.
 *
 * @param importe El importe ya formateado («40,00 €»).
 * @param contexto La forma de trato (`useTrato`), para «¿Aburride?».
 */
export function textoReparto(
  t: TFunction,
  mensaje: MensajeReparto,
  importe: string,
  contexto?: ContextoTrato,
): string {
  switch (mensaje.tipo) {
    case 'demas':
      return t(
        'Water in the hold! {{amount}} is assigned that does not exist. Take some out of a chest.',
        { amount: importe },
      );
    case 'cero':
      return t('Zero. My favourite number.');
    case 'aburre':
      return t('Bored? Me too. Tap Auto.', { context: contexto });
    case 'sigue':
      return t('Good. Another chest. {{amount}} left.', { amount: importe });
    default:
      return '';
  }
}

/** Bocadillo tras pulsar «Auto» en el paso 7. */
export function textoAuto(t: TFunction, habiaQueRepartir: boolean): string {
  const cero = t('Zero. My favourite number.');
  return habiaQueRepartir
    ? `${t('The suggested amount to each chest; whatever is left, to your goal.')} ${cero}`
    : cero;
}

/**
 * Cofres del Plan creados por la travesía, en el orden del catálogo
 * (mensuales, ocasionales y la meta al final), listos para `repartoAuto`.
 */
export function cofresDeTravesia(
  ids: IdsTravesia | undefined,
  /** Con historia: lo que se suele gastar en cada categoría (id real). */
  medias?: ReadonlyMap<string, IntegerAmount>,
): CofreReparto[] {
  const res: CofreReparto[] = [];
  for (const c of [...MENSUALES, ...OCASIONALES]) {
    const id = ids?.categorias?.[c.id];
    if (id) {
      res.push({ id, sugerido: medias?.get(id) ?? sugeridoCofre(c.id) });
    }
  }
  if (ids?.meta) {
    res.push({ id: ids.meta.id, sugerido: ids.meta.cuota, esMeta: true });
  }
  return res;
}
